import {UISTATES} from '../enum';

function isTermDifferent(charA, charB) {
    return charA && charB && charA.toLowerCase() !== charB.toLowerCase();
}

function getMatchedSuggestion(fullMatch, term, search, suggestion) {
    if (fullMatch) {
        return suggestion + term.substr(search.length);
    }
    return '';
}

/**
 * getHintSuggestion()
 * Returns `term` if it contains `search` (case insensitive).  `term` is returned in the same casing as `search`
 * @param {string} search
 * @param {string} term
 */
export function getHintSuggestion(search, term) {
    if (!search || !term) {
        return '';
    }
    let fullMatch = true;
    let hintSuggestion = '';
    for (let i = 0; i < search.length; i++) {
        let searchChar = search[i];
        if (isTermDifferent(searchChar, term[i])) {
            fullMatch = false;
            break;
        } else {
            hintSuggestion += searchChar;
        }
    }
    return getMatchedSuggestion(fullMatch, term, search, hintSuggestion);
}

/**
 * focusSearchInput()
 * Maintain focus on searchinput when in select destination state
 * @param {*} state
 */
export function focusSearchInput(state) {
    if (state.uistate !== UISTATES.SELECT_DESTINATION) {
        return {uistate: UISTATES.SELECT_DESTINATION};
    }
    return false;
}

/**
 * highlightResultNoncontiguous()
 * Enhance returned results with computed highlights
 * TODO: optimize o(n^2) algorithm
 */
function highlightResultNoncontiguous(search, computedResult) {
    const placeFullname = computedResult.term;
    let index = 0;
    for (let i = 0; i < placeFullname.length; i++) {
        let highlighted =
            index < search.length &&
            placeFullname[i].toLowerCase() === search[index].toLowerCase();
        if (highlighted) {
            index++;
        }
        computedResult.highlightedTerm[i] = {
            value: placeFullname[i],
            highlighted
        };
    }
    return computedResult;
}

function addHighlihtRange(highlightRanges, searchIndex, placeFullname, token) {
    const nonAlphaNumericRegex = /[^A-Za-z0-9]+/;
    if (
        searchIndex === 0 ||
        nonAlphaNumericRegex.test(placeFullname[searchIndex - 1])
    ) {
        highlightRanges.push({
            start: searchIndex,
            end: searchIndex + token.length,
            match: true
        });
    }
}

function getTokenMatches(token, placeFullname) {
    const maxIndex = placeFullname.length - token.length + 1;
    const highlightRanges = [];
    let searchIndex = 0;

    while (searchIndex > -1 && searchIndex <= maxIndex) {
        searchIndex = placeFullname.indexOf(token, searchIndex);
        if (searchIndex > -1) {
            addHighlihtRange(
                highlightRanges,
                searchIndex,
                placeFullname,
                token
            );
            searchIndex += token.length;
        }
    }
    return highlightRanges;
}

/* eslint-disable complexity */
function reduceAndMergeRanges(highlightRanges, placeFullname) {
    // reduce ranges and fill in non-match ranges
    // assign start and end for cur range
    let start = highlightRanges[0].start;
    let end = highlightRanges[0].end;
    let finalRanges = [];
    // if start is not at 0, add non-match range
    if (start > 0) {
        finalRanges.push({start: 0, end: start, match: false});
    }
    for (let i = 1; i < highlightRanges.length; i++) {
        const range = highlightRanges[i];
        // if next range start is between cur range start and end, check next range end
        if (range.start >= start && range.start <= end) {
            // if next range end is after cur range end, update end to next range end
            if (range.end > end) {
                end = range.end;
            }
        } else {
            // next range start is after cur range end, add cur range to array
            finalRanges.push({start, end, match: true});
            // add non-match range for space between last range end and next range start
            finalRanges.push({
                start: end,
                end: range.start,
                match: false
            });
            // update start and end vars using next range values
            start = range.start;
            end = range.end;
        }
    }
    // add the last range in source ragnes array
    finalRanges.push({start, end, match: true});
    // if the last range in the source ranges array does not go to the end of the placename
    // string, add a non-match range that goes to the end of the placename string
    if (end < placeFullname.length) {
        finalRanges.push({
            start: end,
            end: placeFullname.length,
            match: false
        });
    }
    return finalRanges;
}
/* eslint-enable complexity */

function highlightResultContiguous(search, computedResult) {
    const nonAlphaNumericRegex = /[^A-Za-z0-9]+/;
    const searchTokenized = search
        .split(nonAlphaNumericRegex)
        .filter((token) => !!token);
    const placeFullname = computedResult.term;
    let highlightRanges = [];

    // get all the ranges from all token matchs in the place name
    searchTokenized.forEach((token) => {
        highlightRanges = [
            ...highlightRanges,
            ...getTokenMatches(token.toLowerCase(), placeFullname.toLowerCase())
        ];
    });

    // sort ranges based on start
    highlightRanges.sort((a, b) => {
        if (a.start === b.start) {
            return 0;
        }
        return a.start < b.start ? -1 : 1;
    });

    // reduce, and merge the ranges into a unique set with match and non-match ranges
    const finalRanges =
        highlightRanges.length > 0
            ? reduceAndMergeRanges(highlightRanges, placeFullname)
            : [
                  {
                      start: 0,
                      end: placeFullname.length,
                      match: false
                  }
              ];

    // build markup from ranges
    finalRanges.forEach((finalRange, index) => {
        computedResult.highlightedTerm[index] = {
            value: placeFullname.slice(finalRange.start, finalRange.end),
            highlighted: finalRange.match
        };
    });

    return computedResult;
}

/**
 * highlightResults()
 * Enhance returned results with computed highlights
 * TODO: optimize o(n^2) algorithm
 */
export function highlightResults(
    search,
    results,
    matchMode = 'non-contiguous'
) {
    let dupeDict = {}; // do not compute dupes
    let computedResults = [];
    let computedResult = null;

    for (let i = 0; i < results.length; i++) {
        let result = results[i];
        let previouslyComputedResult = dupeDict[result.uuid];
        if (previouslyComputedResult) {
            // Continue in loop and do not re-compute highlight
            continue;
        } else {
            computedResult = {highlightedTerm: [], ...result};
            dupeDict[result.uuid] = computedResult;
        }

        // highlight using one of the methods determined by config
        const highlightFunc =
            matchMode === 'non-contiguous'
                ? highlightResultNoncontiguous
                : highlightResultContiguous;
        computedResults.push(highlightFunc(search, computedResult));
    }
    return computedResults;
}

/**
 * isValidPropertyId()
 * Returns boolean to indicate if it's a valid property id
 * Valid property ids:
 * p1078525vb 1078525vb 1078525 ...
 * more suffixes you can find here:
 * https://github.homeawaycorp.com/hacommons/global-config-service/blob/master/data/src/main/resources/global-config/common/data-stores.json
 * @param {string} search
 */
export function isValidPropertyId(search) {
    return search.match(/^p?(\d+)(ha|vb|tr|a|d|o|r|h|)$/) !== null;
}
